import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const serviceCategories = [
    'Beauty & Wellness',
    'Home Services',
    'Medical Care',
    'Events & Entertainment',
    'Transportation',
    'Education & Tutoring',
    'Legal Services',
    'Professional Services',
  ];

  const markets = [
    { name: 'Dominican Republic', status: 'Launch Market', flag: '🇩🇴', year: '2023' },
    { name: 'New York', status: 'Active', flag: '🇺🇸', year: '2024' },
    { name: 'Puerto Rico', status: 'Active', flag: '🇵🇷', year: '2024' },
    { name: 'Miami', status: 'Active', flag: '🇺🇸', year: '2024' },
  ];

  const futureMarkets = [
    { name: 'Colombia', flag: '🇨🇴' },
    { name: 'Brazil', flag: '🇧🇷' },
    { name: 'Chile', flag: '🇨🇱' },
    { name: 'Mexico', flag: '🇲🇽' },
  ];

  const missionPoints = [
    { title: 'Visible', description: 'Making service providers discoverable in the digital economy' },
    { title: 'Trusted', description: 'Building reputation systems that create accountability' },
    { title: 'Monetizable', description: 'Enabling digital payments and financial inclusion' },
    { title: 'Technology-Enabled', description: 'Providing tools for modern business management' },
    { title: 'Scalable', description: 'Creating infrastructure that grows with businesses' },
    { title: 'Formalized', description: 'Transitioning informal workers into the formal economy' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
              About United Empresas
            </p>
            <h1 className="text-hero text-foreground mb-6">
              The Vision Behind UE
            </h1>
            <p className="text-body-large">
              We're building the marketplace infrastructure that will formalize, digitize,
              and scale Latin America's $1.2 trillion service economy.
            </p>
          </div>
        </div>
      </section>

      {/* What We're Building */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-display text-foreground mb-6">
                What UE is Building
              </h2>
              <p className="text-body-large mb-8">
                A comprehensive marketplace infrastructure that enables service discovery,
                booking, payment processing, reputation management, and customer retention—
                all in one unified platform.
              </p>
              <p className="text-muted-foreground mb-8">
                Our platform serves as the operating system for local service businesses,
                providing the technology infrastructure they need to compete in the modern economy.
              </p>
              <Link
                to="/technology"
                className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary-light transition-colors"
              >
                Explore our technology
                <Icon name="ArrowRight" size={16} />
              </Link>
            </div>

            <div className="bg-white rounded-brand-lg p-8 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Service Categories
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {serviceCategories.map((category, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-brand-sm">
                    <Icon name="Check" size={16} className="text-accent" />
                    <span className="text-sm text-foreground">{category}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                And expanding into new verticals as we grow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Geographic Footprint */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Geographic Footprint
            </h2>
            <p className="text-body-large">
              Starting from our launch market in the Dominican Republic, we're expanding
              across the Americas to build the largest service marketplace network in the region.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Active Markets */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-6">Active Markets</h3>
              <div className="space-y-4">
                {markets.map((market, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-brand">
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{market.flag}</span>
                      <div>
                        <div className="font-medium text-foreground">{market.name}</div>
                        <div className="text-sm text-muted-foreground">{market.status}</div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Since {market.year}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expansion Roadmap */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-6">Expansion Roadmap</h3>
              <div className="bg-primary rounded-brand p-8 text-white h-full">
                <p className="text-white/70 mb-6">
                  Our expansion strategy focuses on markets with large informal service economies
                  and strong demand for digital transformation.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {futureMarkets.map((market, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/10 rounded-brand-sm">
                      <span className="text-2xl">{market.flag}</span>
                      <span className="text-sm">{market.name}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/investor-relations"
                  className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-white/80 transition-colors"
                >
                  View expansion timeline
                  <Icon name="ArrowRight" size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
              Our Mission
            </p>
            <h2 className="text-display text-foreground mb-6">
              Making the Service Economy in Latin America...
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {missionPoints.map((point, index) => (
              <div key={index} className="bg-white rounded-brand p-6 border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {point.title}
                </h3>
                <p className="text-muted-foreground">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-narrow mx-auto px-4 lg:px-8 text-center">
          <blockquote className="text-2xl lg:text-3xl font-medium text-white leading-relaxed mb-8">
            "We are not building a booking app. We are building infrastructure for a
            trillion-dollar informal economy that has been left behind by technology."
          </blockquote>
          <div className="text-white/60">
            — Founding Team, United Empresas
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/corporate-structure"
              className="group p-6 bg-muted rounded-brand hover:bg-white hover:shadow-corporate-md hover:border-border border border-transparent transition-all duration-300"
            >
              <Icon name="Building" size={24} className="text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Corporate Structure</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Learn about our corporate entities and governance.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-primary group-hover:text-primary-light transition-colors">
                View structure
                <Icon name="ArrowRight" size={14} className="ml-2" />
              </span>
            </Link>

            <Link
              to="/technology"
              className="group p-6 bg-muted rounded-brand hover:bg-white hover:shadow-corporate-md hover:border-border border border-transparent transition-all duration-300"
            >
              <Icon name="Code" size={24} className="text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Technology</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Explore our marketplace infrastructure and tech stack.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-primary group-hover:text-primary-light transition-colors">
                View technology
                <Icon name="ArrowRight" size={14} className="ml-2" />
              </span>
            </Link>

            <Link
              to="/impact"
              className="group p-6 bg-muted rounded-brand hover:bg-white hover:shadow-corporate-md hover:border-border border border-transparent transition-all duration-300"
            >
              <Icon name="Heart" size={24} className="text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Impact</h3>
              <p className="text-sm text-muted-foreground mb-4">
                See how we're transforming communities and lives.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-primary group-hover:text-primary-light transition-colors">
                View impact
                <Icon name="ArrowRight" size={14} className="ml-2" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
